import React, { useEffect } from 'react'

import styles from './action-buttons.module.scss'
import { Icon } from '@iconify/react'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import type { SaveStatus } from 'features/Procedure/hooks/useSaveDrawing'
import { useAppSelector } from 'common/hooks/redux'
import { DrawMode } from 'features/Procedure/state/procedure.type'
import classNames from 'classnames'
import { ModelSceneToggleButton } from 'features/Procedure/components/model-scene-toggle-button'

type Props = {
  onToggleFullscreen?: () => void
  onToggleModel?: () => void
  isFullscreen?: boolean
  isLoading?: boolean
  saveStatus?: SaveStatus
  hasRemoteModelVersion?: boolean
  hasDemographicGender?: boolean
  autosaveDelayMs?: number
  onIncreaseAutosaveDelay?: () => void
  onDecreaseAutosaveDelay?: () => void
  selectedPatientId: number | null
  hide?: boolean
  disableActions?: boolean
}

export const ActionButtons = React.memo(
  ({
    onToggleFullscreen,
    onToggleModel,
    isLoading = false,
    isFullscreen = false,
    saveStatus = 'idle',
    hasRemoteModelVersion = false,
    hasDemographicGender = false,
    selectedPatientId,
    hide = false,
    disableActions = false,
  }: Props) => {
    const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
    const selectedEncounterUid = useAppSelector(
      (state) => state.procedureReducer.selectedEncounterUid,
    )
    const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
    const showElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
    const { handleToggleDrawMode, handleToggleShowElementIds, handleSetDrawMode } =
      useSceneActions()

    const hasProcedures = !!selectedProcedure

    useEffect(() => {
      if (!hasProcedures) {
        handleSetDrawMode(null)
      }
    }, [hasProcedures, handleSetDrawMode])

    const isActionDisabled =
      !selectedEncounterUid || !selectedProcedure || isLoading || disableActions

    const drawModeTitles: Record<DrawMode, string> = {
      [DrawMode.PIN]: 'Pin mode',
      [DrawMode.FREE_LINE]: 'Free line mode',
      [DrawMode.STRAIGHT_LINE]: 'Straight line mode',
      [DrawMode.X]: 'X mode',
      [DrawMode.MEASURE]: 'Measure mode',
      [DrawMode.ERASER]: 'Eraser mode',
    }

    const getCommonButtonProps = (mode: DrawMode) => ({
      className: classNames(styles.actionButton, {
        [styles.isActive]: drawMode === mode,
      }),
      onClick: () => handleToggleDrawMode(mode),
      key: mode,
      disabled: isActionDisabled,
      title: drawModeTitles[mode],
      type: 'button' as const,
    })

    const shouldShowSaveStatus = !hide && (saveStatus === 'saving' || saveStatus === 'saved')

    return (
      <>
        {shouldShowSaveStatus && (
          <div className={styles.saveStatusContainer}>
            {saveStatus === 'saving' && (
              <div className={styles.savingIndicator}>
                Saving
                <span className={styles.dots} />
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className={classNames(styles.savingIndicator, styles.saved)}>
                Saved
                <Icon icon='mdi:check' fontSize={16} />
              </div>
            )}
          </div>
        )}
        <div className={styles.container}>
          {/* {ModelDebugMode &&
          autosaveDelayMs !== undefined &&
          onIncreaseAutosaveDelay &&
          onDecreaseAutosaveDelay && (
            <AutosaveDebugWidget
              autosaveDelayMs={autosaveDelayMs}
              onIncreaseDelay={onIncreaseAutosaveDelay}
              onDecreaseDelay={onDecreaseAutosaveDelay}
            />
          )} */}
          {!hide && (
            <>
              <button {...getCommonButtonProps(DrawMode.PIN)} key={DrawMode.PIN}>
                <Icon icon='cil:location-pin' fontSize={16} />
              </button>
              <button {...getCommonButtonProps(DrawMode.FREE_LINE)} key={DrawMode.FREE_LINE}>
                <Icon icon='mdi-light:pencil' fontSize={16} />
              </button>
              <button
                {...getCommonButtonProps(DrawMode.STRAIGHT_LINE)}
                key={DrawMode.STRAIGHT_LINE}>
                <Icon icon='lets-icons:line-out' fontSize={16} />
              </button>
              <button {...getCommonButtonProps(DrawMode.X)} key={DrawMode.X}>
                <Icon icon='mdi:close' fontSize={16} />
              </button>
              <button {...getCommonButtonProps(DrawMode.MEASURE)} key={DrawMode.MEASURE}>
                <Icon icon='lsicon:measure-filled' fontSize={16} />
              </button>
              <button {...getCommonButtonProps(DrawMode.ERASER)} key={DrawMode.ERASER}>
                <Icon icon='mdi:eraser' fontSize={16} />
              </button>

              <button
                className={classNames(styles.actionButton, {
                  [styles.isActive]: showElementIds,
                })}
                onClick={handleToggleShowElementIds}
                disabled={isActionDisabled}
                type='button'>
                <Icon icon='mdi:tag-outline' fontSize={16} />
              </button>

              {onToggleModel && !hasRemoteModelVersion && !hasDemographicGender && (
                <button
                  className={styles.actionButton}
                  onClick={onToggleModel}
                  title='Switch to male next model'
                  disabled={!selectedPatientId || isLoading}
                  type='button'>
                  <Icon icon='mdi:account-switch' fontSize={16} />
                </button>
              )}

              {onToggleFullscreen && (
                <button
                  className={classNames(styles.actionButton, styles.fullscreenButton, {
                    [styles.isActive]: isFullscreen,
                  })}
                  onClick={onToggleFullscreen}
                  type='button'>
                  <Icon
                    icon={isFullscreen ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'}
                    fontSize={16}
                  />
                </button>
              )}
            </>
          )}
          <ModelSceneToggleButton className={styles.actionButton} />
        </div>
      </>
    )
  },
)
